import React, { useState } from "react";
import {
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Column, kanbanStyles as styles } from "./constants";

// ─── Inline quick-add card ────────────────────────────────────────────────────

function QuickAddCard({ onAdd }: { onAdd: (title: string) => void }) {
  const [active, setActive] = useState(false);
  const [title, setTitle] = useState("");

  const commit = () => {
    const trimmed = title.trim();
    if (trimmed) onAdd(trimmed);
    setTitle("");
    setActive(false);
  };

  const cancel = () => {
    setTitle("");
    setActive(false);
  };

  if (!active) {
    return (
      <TouchableOpacity
        style={qaStyles.trigger}
        onPress={() => setActive(true)}
      >
        <MaterialCommunityIcons name="plus" size={16} color="#6B7280" />
        <Text style={qaStyles.triggerText}>Add card</Text>
      </TouchableOpacity>
    );
  }

  return (
    <View style={qaStyles.card}>
      <TextInput
        autoFocus
        value={title}
        onChangeText={setTitle}
        placeholder="Card title…"
        placeholderTextColor="#9CA3AF"
        multiline
        style={qaStyles.input}
        // Commit on enter (non-multiline submit)
        onSubmitEditing={commit}
        blurOnSubmit={false}
      />
      <View style={qaStyles.row}>
        <TouchableOpacity style={qaStyles.addBtn} onPress={commit}>
          <Text style={qaStyles.addBtnText}>Add card</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={cancel} style={qaStyles.cancelBtn}>
          <MaterialCommunityIcons name="close" size={18} color="#6B7280" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const qaStyles = StyleSheet.create({
  trigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 2,
  },
  triggerText: {
    fontSize: 14,
    color: "#6B7280",
    fontWeight: "500",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: 10,
    marginTop: 6,
    shadowColor: "#000",
    shadowOpacity: 0.07,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  input: {
    fontSize: 14,
    color: "#1E293B",
    minHeight: 56,
    textAlignVertical: "top",
    marginBottom: 8,
    borderWidth: 1.5,
    borderColor: "#3B82F6",
    borderRadius: 6,
    padding: 8,
    backgroundColor: "#EFF6FF",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  addBtn: {
    backgroundColor: "#3B82F6",
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 6,
  },
  addBtnText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 13,
  },
  cancelBtn: {
    padding: 4,
  },
});

// ─── Sortable Column ──────────────────────────────────────────────────────────

export default function SortableColumn({
  column,
  children,
  onEdit,
  onDelete,
  onAddTask, // now called with (columnId, title) — signature change below
}: {
  column: Column;
  children: React.ReactNode;
  onEdit: (column: Column) => void;
  onDelete: (columnId: string) => void;
  onAddTask: (columnId: string, title: string) => void;
}) {
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: column.id,
  });

  const {
    setNodeRef: setSortableRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <View
      ref={setSortableRef}
      style={[styles.column, isOver && styles.columnHighlighted, style as any]}
    >
      {/* Column header — drag handle lives here */}
      <View
        style={[styles.columnHeader, { backgroundColor: column.color }]}
        {...attributes}
        {...listeners}
      >
        <Text style={styles.columnTitle}>{column.title}</Text>
        <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
          <View style={styles.columnBadge}>
            <Text style={styles.columnBadgeText}>{column.tasks.length}</Text>
          </View>
          <TouchableOpacity onPress={() => onEdit(column)}>
            <MaterialCommunityIcons name="pencil" size={18} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => onDelete(column.id)}>
            <MaterialCommunityIcons name="delete" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Task list */}
      <ScrollView
        ref={setDroppableRef}
        style={styles.columnContent}
        showsVerticalScrollIndicator={false}
      >
        {children}

        {/* Inline quick-add at bottom of task list */}
        <QuickAddCard onAdd={(title) => onAddTask(column.id, title)} />
      </ScrollView>
    </View>
  );
}
